import { priceFilterAmounts } from './price-filter-amounts';
import { rememberCollectionResultsDestination, requestCollectionResultsScroll } from './collection/results-scroll';

// Buttons "Price Filter" — segmented shop-by-price bar that drives the
// collection grid's Algolia price refinement in place. Each pill is matched
// to a sidebar price option (InstantSearch numericMenu radio) by the set of
// dollar amounts in its label ("Under $1k" ↔ "UNDER $1,000"), so wording can
// differ between the bar and the sidebar. Clicking an active pill re-selects
// the "All" option. When no price widget exists on the page, pills fall back
// to navigating to their Button Link.

(function () {
  const bars = document.querySelectorAll<HTMLElement>('.js-price-filter-bar');
  if (!bars.length) return;

  const brand = document.querySelector<HTMLElement>('[data-watch-brand-intro]')?.dataset.watchBrandIntro;
  const path = window.location.pathname.replace(/^\/fr(?=\/)/, '').replace(/\/$/, '');
  const marker = document.querySelector('meta[name="raffi-native-release"][content="20261008"]')
    || document.querySelector('meta[name="raffi-native-preview"][content="20261008"]');
  const frenchBrand = !!marker && /^fr(?:-|$)/i.test(document.documentElement.lang)
    && !!brand && !/rolex/i.test(brand) && path === '/collections/timepieces/' + brand;
  const amounts = (label: string): string => priceFilterAmounts(label, frenchBrand);
  const nativeCatalogue = !!document.querySelector('main[data-watch-buying="collection"]')
    && !/(?:^|\/)rolex(?:[-/]|$)/i.test(window.location.pathname);

  const priceRadios = (): HTMLInputElement[] => {
    const menus = document.querySelectorAll<HTMLElement>('.ais-NumericMenu');
    for (const menu of Array.from(menus)) {
      const inputs = Array.from(menu.querySelectorAll<HTMLInputElement>('input[type="radio"]'));
      const labels = inputs.map((input) => amounts(input.closest('label')?.textContent || ''));
      // the price menu is the one whose options mention dollar amounts
      if (labels.filter(Boolean).length >= 2) return inputs;
    }
    return [];
  };

  const radioFor = (pillKey: string): HTMLInputElement | null =>
    priceRadios().find((input) => amounts(input.closest('label')?.textContent || '') === pillKey) || null;

  bars.forEach((bar) => {
    const pills = Array.from(bar.querySelectorAll<HTMLButtonElement>('.js-price-pill'));

    const sync = () => {
      pills.forEach((pill) => {
        if (pill.closest('main[data-timepiece-filter-journey]') && pill.dataset.timepieceChoice === 'price') return;
        const radio = radioFor(amounts(pill.textContent || ''));
        const active = !!radio && radio.checked;
        pill.classList.toggle('is-active', active);
        pill.setAttribute('aria-pressed', String(active));
      });
    };

    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        if (pill.closest('main[data-timepiece-filter-journey]') && pill.dataset.timepieceChoice === 'price') return;
        const key = amounts(pill.textContent || '');
        const radios = priceRadios();
        if (!radios.length) {
          const fallback = pill.dataset.fallback;
          if (fallback) {
            const destination = rememberCollectionResultsDestination(fallback);
            window.location.href = destination || fallback;
          }
          return;
        }
        const target = pill.classList.contains('is-active')
          ? radios.find((input) => !amounts(input.closest('label')?.textContent || '')) // "All"
          : radioFor(key);
        const deferred = target ? requestCollectionResultsScroll(!target.checked) : false;
        if (target && !target.checked) target.click();
        const grid = document.getElementById('collectionWrapper');
        if (grid && !deferred) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // the widget re-renders asynchronously after refinement
        setTimeout(sync, 250);
        setTimeout(sync, 900);
      });
    });

    // stay in sync when the sidebar options are used directly
    document.addEventListener('change', (event) => {
      if ((event.target as HTMLElement).closest?.('.ais-NumericMenu')) setTimeout(sync, 250);
    });
    window.addEventListener('popstate', () => setTimeout(sync, 400));
    // Clear all and removed refinements update Algolia without a radio change.
    // Read the rendered numeric-menu state after every native watch render.
    if (nativeCatalogue) document.addEventListener('native-watch-filters-rendered', sync);

    // initial state (widgets mount after page load)
    setTimeout(sync, 800);
    setTimeout(sync, 2500);
  });
})();
